import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

export const useActivities = (skip = false) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Fetch activities with optional filters
  const fetchActivities = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      // Default to high limit so all activities are returned (fixes pagination issue)
      const params = { limit: 9999, ...filters };
      const response = await apiService.getActivities(params);
      setActivities(response.activities || []);
      return response;
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch activities:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.getStats();
      setStats(response);
      return response;
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      throw err;
    }
  }, []);

  // Create new activity
  const createActivity = useCallback(async (activityData) => {
    try {
      const newActivity = await apiService.createActivity(activityData);
      setActivities(prev => [newActivity, ...prev]);
      await fetchStats(); // Refresh stats
      return newActivity;
    } catch (err) {
      setError(err.message);
      console.error('Failed to create activity:', err);
      throw err;
    }
  }, [fetchStats]);

  // Update activity
  const updateActivity = useCallback(async (id, activityData) => {
    try {
      const updatedActivity = await apiService.updateActivity(id, activityData);
      setActivities(prev => 
        prev.map(activity => 
          activity._id === id ? { ...updatedActivity, id: updatedActivity._id } : activity
        )
      );
      await fetchStats(); // Refresh stats
      return updatedActivity;
    } catch (err) {
      setError(err.message);
      console.error('Failed to update activity:', err);
      throw err;
    }
  }, [fetchStats]);

  // Delete activity
  const deleteActivity = useCallback(async (id) => {
    try {
      await apiService.deleteActivity(id);
      setActivities(prev => prev.filter(activity => (activity._id || activity.id) !== id));
      await fetchStats(); // Refresh stats
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete activity:', err);
      throw err;
    }
  }, [fetchStats]);

  // Initial data fetch — skip jika belum login (parameter skip = true)
  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    const initializeData = async () => {
      try {
        await Promise.all([
          fetchActivities(),
          fetchStats()
        ]);
      } catch (err) {
        setError(err.message);
      }
    };

    initializeData();
  }, [fetchActivities, fetchStats, skip]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    activities,
    loading,
    error,
    stats,
    fetchActivities,
    fetchStats,
    createActivity,
    updateActivity,
    deleteActivity,
    clearError
  };
};
